import { useSyncExternalStore } from 'react';
import { getBulkUploadTimestamp } from '../services/bulkUploadTimestamp';

const listeners = new Set();

let state = {
  uploads: [],
  uploadsById: new Map(),
};

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const shallowEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right) return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
};

const mergeUpload = (existing, incoming) => {
  if (!existing) {
    return incoming;
  }
  const merged = { ...existing, ...incoming };
  return shallowEqual(existing, merged) ? existing : merged;
};

const sortUploads = (uploads) => (
  uploads
    .map((upload, index) => ({ upload, index }))
    .sort((left, right) => {
      const diff = getBulkUploadTimestamp(right.upload) - getBulkUploadTimestamp(left.upload);
      if (diff !== 0) {
        return diff;
      }
      return left.index - right.index;
    })
    .map(({ upload }) => upload)
);

const replaceState = (nextUploads) => {
  const nextMap = new Map(nextUploads.map((upload) => [upload.id, upload]));
  const isSameLength = nextUploads.length === state.uploads.length;
  const isSameOrder = isSameLength && nextUploads.every((upload, index) => upload === state.uploads[index]);

  if (isSameOrder) {
    return;
  }

  state = {
    uploads: nextUploads,
    uploadsById: nextMap,
  };
  emitChange();
};

export const setUploads = (uploads = []) => {
  if (!Array.isArray(uploads)) {
    replaceState([]);
    return;
  }

  const nextUploads = sortUploads(uploads
    .filter((upload) => upload && upload.id)
    .map((upload) => mergeUpload(state.uploadsById.get(upload.id), upload)));

  replaceState(nextUploads);
};

export const mergeUploads = (uploads = []) => {
  if (!Array.isArray(uploads)) {
    return;
  }

  const nextMap = new Map(state.uploadsById);
  uploads
    .filter((upload) => upload && upload.id)
    .forEach((upload) => {
      const merged = mergeUpload(nextMap.get(upload.id), upload);
      nextMap.set(upload.id, merged);
    });

  replaceState(sortUploads(Array.from(nextMap.values())));
};

export const upsertUpload = (upload, { prepend = false } = {}) => {
  if (!upload || !upload.id) {
    return;
  }

  const existing = state.uploadsById.get(upload.id);
  const merged = mergeUpload(existing, upload);
  if (existing && merged === existing) {
    return;
  }

  let nextUploads;
  if (existing) {
    nextUploads = state.uploads.map((item) => (item.id === upload.id ? merged : item));
  } else if (prepend) {
    nextUploads = [merged, ...state.uploads];
  } else {
    nextUploads = [...state.uploads, merged];
  }

  replaceState(sortUploads(nextUploads));
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getUploadsSnapshot = () => state.uploads;
const getUploadSnapshot = (uploadId) => state.uploadsById.get(uploadId) || null;

export const useBulkUploads = () => useSyncExternalStore(subscribe, getUploadsSnapshot);

export const useBulkUploadRow = (uploadId) => (
  useSyncExternalStore(subscribe, () => getUploadSnapshot(uploadId))
);
